// imports
import React, { useRef, useState } from 'react';
import { Box, Paper, Popper, Tooltip } from '@mui/material';
import { BuildOutlined, Close } from '@mui/icons-material';

// local imports
import CustomButton from '../../CustomButton';
import { sharedTooltipProps } from './sharedTooltipProps';

interface CollapsedMapToolsProps {
  children: React.ReactNode;
}

/**
 * CollapsedMapTools
 *
 * Renders a toggle button that opens a popover with map tool buttons.
 * Used when the viewport is too short to display all controls individually.
 * Uses disablePortal so the popup renders inline in the DOM.
 * Focus traps between the popup buttons and the toggle/close button.
 *
 * @returns {JSX.Element} The rendered collapsed map tools component.
 */
const CollapsedMapTools = React.memo(function CollapsedMapTools({
  children,
}: CollapsedMapToolsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const popoverRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const getFocusableElements = () =>
    popoverRef.current?.querySelectorAll<HTMLElement>(
      'button, [tabindex]:not([tabindex="-1"])'
    );

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent the button from gaining focus when opening,
    // so the screen reader doesn't announce the intermediate "expanded" state
    if (!anchorEl) {
      e.preventDefault();
    }
  };

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
      toggleButtonRef.current?.focus();
    } else {
      setAnchorEl(e.currentTarget);
      setTimeout(() => {
        getFocusableElements()?.[0]?.focus();
      });
    }
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setAnchorEl(null);
      toggleButtonRef.current?.focus();
      return;
    }
    if (e.key === 'Tab' && e.shiftKey) {
      const focusable = getFocusableElements();
      if (focusable?.length && document.activeElement === focusable[0]) {
        e.preventDefault();
        toggleButtonRef.current?.focus();
      }
    }
  };

  const handleToggleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || e.key !== 'Tab') return;
    if (!e.shiftKey) {
      e.preventDefault();
      getFocusableElements()?.[0]?.focus();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="left-start"
        disablePortal
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: ({ reference }: { reference: { width: number; height: number } }) => [reference.height / 2, 10] } }]}
      >
        <Paper
          ref={popoverRef}
          elevation={8}
          onKeyDown={handlePopoverKeyDown}
          role="group"
          aria-label="Map tools"
          sx={{
            p: 1,
            opacity: 0.9,
            position: 'relative',
            borderRadius: '4px 0 4px 4px',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: -11,
              width: 0,
              height: 0,
              borderTop: '0px solid transparent',
              borderBottom: '20px solid transparent',
              borderLeft: '12px solid',
              borderLeftColor: 'background.paper',
              filter: 'drop-shadow(2px 0px 1px rgba(0,0,0,0.1))',
            },
          }}
        >
          <Box sx={{ display: 'flex', gap: '10px' }}>{children}</Box>
        </Paper>
      </Popper>
      <Tooltip
        title={isOpen ? 'Close map tools' : 'Map tools'}
        placement="left"
        {...sharedTooltipProps}
      >
        <CustomButton
          ref={toggleButtonRef}
          data-testid="map-tools-button"
          aria-label={isOpen ? 'Close map tools' : 'Map tools'}
          aria-expanded={isOpen}
          buttonVariant="standalone"
          onClick={handleToggle}
          onMouseDown={handleMouseDown}
          onKeyDown={handleToggleKeyDown}
        >
          {isOpen ? <Close /> : <BuildOutlined />}
        </CustomButton>
      </Tooltip>
    </Box>
  );
});

export default CollapsedMapTools;
